import React, { useState, useEffect, useContext } from 'react'
import { HeaderContext } from '../App'
import { connect } from 'react-redux'
import Checkbox from './Checkbox'
import recaptcha from '~/helpers/loadRecaptcha'
import http, { preparePayload, formatErrors, wrapButtonToggle } from '~/http'
import { initialState as blankContact } from '~/reducers/defaultContact'
import states from '~/constants/states'
import ErrorMessage from '~/components/ErrorMessage'
import ErrorList from '~/components/ErrorList'
import SVGIcon from '~/components/SVGIcon'
import Button from '~/components/Button'
import Select from '~/components/Select'
import TextField from './TextField'
import DatePicker from '~/components/DatePicker'
import Banner from '~/components/Banner'
import { ageToday } from '~/helpers/dateAndTime'
import { formatPhoneNumber } from '~/helpers/phoneNumber'
import useScreenResize from '~/custom-hooks/useScreenResize'
import './AddStudentPage.scss'

// --vars

const relationships = {
  Parent: 'Parent',
  Guardian: 'Guardian',
  Spouse: 'Spouse',
  Companion: 'Companion',
  Grandparent: 'Grandparent',
  Stepparent: 'Stepparent',
  Sibling: 'Sibling',
  Child: 'Child',
  Stepchild: 'Stepchild',
  Grandchild: 'Grandchild',
  Friend: 'Friend',
  Other: 'Other'
}

const genders = {
  Female: 'Female',
  Male: 'Male',
  Other: 'Other'
}

const phoneTypes = {
  Mobile: 'Mobile',
  Work: 'Work',
  Residential: 'Residential'
}

const raceList = ['American Indian/Alaskan Native', 'Asian', 'Black or African American', 'Hawaiian/Pacific Islander', 'White', 'Middle Eastern or North African', 'Unknown']

// Array of json objects with an ID key and a language key
const makeOptions = obj => Object.keys(obj).reduce((acc, x) => {
  acc.push({ value: x, label: obj[x] })
  return acc
}, [])

const stateOptions = makeOptions(states)
const relationshipOptions = makeOptions(relationships)
const genderOptions = makeOptions(genders)
const phoneTypeOptions = makeOptions(phoneTypes)

// --components
function AddStudentPage (props) {
  const [globalError, setGlobalError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [lastName, setLastName] = useState('')
  const [gender, setGender] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [address1, setAddress1] = useState(props.defaultAddress.address1)
  const [address2, setAddress2] = useState(props.defaultAddress.address2)
  const [city, setCity] = useState(props.defaultAddress.city)
  const [state, setState] = useState(props.defaultAddress.state)
  const [zipCode, setZipCode] = useState(props.defaultAddress.zipCode)
  const [hasAttendedBefore, setHasAttendedBefore] = useState(false)
  const [notes, setNotes] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneType, setPhoneType] = useState('')
  const [optOutOfTextMessages, setOptOutOfTextMessages] = useState(false)
  const [email, setEmail] = useState('')
  const [isAdult, setIsAdult] = useState(false)
  const [currentAge, setCurrentAge] = useState(null)
  const [educationLevel, setEducationLevel] = useState('')
  const [educationLevels, setEducationLevels] = useState([])
  const [races, setRaces] = useState([])
  const [isHispanicLatino, setIsHispanicLatino] = useState(false)
  const [language, setLanguage] = useState(undefined)
  const [languageOptions, setLanguageOptions] = useState([])
  const [companySponsorshipInfo, setCompanySponsorshipInfo] = useState({
    companyGaveTimeOff: false,
    companyIsPaying: false,
    companyWillReimburse: false,
    selfEmployed: false,
    sponsoringCompanyId: undefined,
    sponsoringCompanyName: '',
    sponsoringCompanyAddress: '',
    sponsoringCompanyCity: '',
    sponsoringCompanyState: '',
    sponsoringCompanyZipCode: '',
    companyName: ' ',
    companyAddress: ' ',
    companyCity: ' ',
    companyState: '',
    companyZipCode: ' ',
    supervisorName: '',
    supervisorEmail: '',
    supervisorPhoneNumber: '',
    employedByDifferentCompany: false
  })
  const [contacts, setContacts] = useState([Object.assign({}, props.defaultContact)])

  const screenResize = useScreenResize()
  const screenSize = screenResize.size

  const updateContact = (index, newValue) => {
    const newContacts = contacts.slice(0)
    newContacts[index] = newValue
    setContacts(newContacts)
  }
  const addContact = () => {
    const newContacts = contacts.slice(0)
    newContacts.push(Object.assign({}, blankContact))
    setContacts(newContacts)
  }
  const removeContact = (index) => {
    const newContacts = contacts.slice(0)
    newContacts.splice(index, 1)
    setContacts(newContacts)
  }

  const getStudent = () => ({
    firstName,
    middleName,
    lastName,
    gender,
    birthDate,
    address1,
    address2,
    city,
    state,
    zipCode,
    phoneNumber,
    phoneType,
    optOutOfTextMessages,
    email,
    hasAttendedBefore,
    notes,
    contacts,
    language,
    educationLevel,
    races,
    isHispanicLatino
  })

  const getPersonOptionData = async () => {
    const response = await http(setGlobalError, http => http.get('/api/SelfService/Students/GetStudentOptionData'))
    const educationLevelOptions = response.data.educationLevels.map(x => {
      return { label: x.name, value: x.id }
    })
    const languages = response.data.languages.map(x => {
      return { label: x.name, value: x.id }
    })

    setEducationLevels(educationLevelOptions)
    setLanguageOptions(languages)
  }

  const { setHeaderInfo } = useContext(HeaderContext)
  const isCartLink = window.location.hash.includes('cart=true')

  useEffect(() => {
    if (birthDate.length === 10) {
      const currentAge = ageToday(birthDate)
      setIsAdult(currentAge >= 16)
      setCurrentAge(currentAge)
      if (currentAge >= 18 && contacts.length === 1 && JSON.stringify(contacts[0]) === JSON.stringify(props.defaultContact)) {
        setContacts([])
      } else if (contacts.length < 1) {
        setContacts([Object.assign({}, props.defaultContact)])
      }
    } else {
      setIsAdult(false)
      setCurrentAge(null)
    }

    const addStudentHash = window.location.hash
    const isWedIndustryParam = addStudentHash.toLowerCase().includes('iswedindustry=true')

    if (isWedIndustryParam) {
      setTimeout(() => {
        props.history.push('/addindustrystudent')
      }, 0)
    }

    getPersonOptionData()
  }, [birthDate])

  useEffect(() => {
    setHeaderInfo({ pageTitle: 'Add Student' })
  }, [])

  const optOutOfTextMessagesComponent = (
    <div className='row'>
      <Checkbox
        id='student-opt-out'
        checked={optOutOfTextMessages}
        onChange={(evt) => setOptOutOfTextMessages(evt.target.checked)}
        label='Opt out of Text Messages?'
      />
    </div>
  )

  return (
    <div className='page add-student'>
      <Banner message='Enroll' />
      <div className='page-body'>
        {globalError && <ErrorMessage message={globalError} />}
        {Object.keys(fieldErrors).length > 0 && <ErrorList errors={fieldErrors} />}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label='First Name *'
                required
                invalid={fieldErrors['First Name'] != null}
                value={firstName}
                onChange={evt => setFirstName(evt.target.value)}
                fullwidth
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label='Middle Name'
                invalid={fieldErrors['Middle Name'] != null}
                value={middleName}
                onChange={evt => setMiddleName(evt.target.value)}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label='First Name *'
              required
              invalid={fieldErrors['First Name'] != null}
              value={firstName}
              onChange={evt => setFirstName(evt.target.value)}
              fullwidth
            />
            <TextField
              className='width-75-percent'
              label='Middle Name'
              invalid={fieldErrors['Middle Name'] != null}
              value={middleName}
              onChange={evt => setMiddleName(evt.target.value)}
            />
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label='Last Name *'
                required
                invalid={fieldErrors['Last Name'] != null}
                value={lastName}
                onChange={evt => setLastName(evt.target.value)}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label='Gender *'
                required
                fieldErrors={fieldErrors}
                options={genderOptions}
                setter={setGender}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label='Last Name *'
              required
              invalid={fieldErrors['Last Name'] != null}
              value={lastName}
              onChange={evt => setLastName(evt.target.value)}
              fullwidth
            />
            <Select
              className='width-100-percent'
              label='Gender *'
              required
              fieldErrors={fieldErrors}
              options={genderOptions}
              setter={setGender}
            />
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <DatePicker
                className='cell'
                name='Birth Date'
                label='DOB '
                required
                fieldErrors={fieldErrors}
                value={birthDate}
                setter={setBirthDate}
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label='Education Level'
                fieldErrors={fieldErrors}
                options={educationLevels}
                setter={setEducationLevel}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <>
            <div className='row anchor-width-50'>
              <DatePicker
                className='cell'
                name='Birth Date'
                label='DOB '
                required
                fieldErrors={fieldErrors}
                value={birthDate}
                setter={setBirthDate}
              />
              <Select
                className='width-50-percent'
                label='Education Level'
                fieldErrors={fieldErrors}
                options={educationLevels}
                setter={setEducationLevel}
              />
            </div>
          </>
        )}

        <div className='row'>
          <h5>Race (check any that apply)</h5>
        </div>
        {
          raceList.map((raceObj, idx) =>
            <div className='row' key={idx}>
              <Checkbox
                id={`${raceObj}-${idx}`}
                label={raceObj}
                checked={races.includes(raceObj)}
                onChange={(evt) => {
                  const isChecked = races.includes(raceObj)
                  const personRaces = [].concat(races)

                  if (isChecked) {
                    personRaces.splice(personRaces.indexOf(raceObj), 1)
                  } else {
                    personRaces.push(raceObj)
                  }

                  setRaces(personRaces.slice(0))
                }}
              />
            </div>
          )
        }

        <div className='row'>
          <h5>Hispanic / Latino</h5>
        </div>
        <div className='row'>
          <Checkbox
            id='latino'
            checked={isHispanicLatino}
            onChange={(evt) => setIsHispanicLatino(evt.target.checked)}
            label='Yes'
          />
        </div>

        <div className='row'>
          <h5>Language</h5>
        </div>
        <div className='row anchor-width-100'>
          <Select
            className='width-100-percent'
            label='Please Select Your Primary Language'
            fieldErrors={fieldErrors}
            options={languageOptions}
            setter={setLanguage}
          />
        </div>

        <div className='row'>
          <h5>Current address and phone number</h5>
        </div>

        {optOutOfTextMessagesComponent}

        <div className='row'>
          <TextField
            className='cell'
            label={`Email${isAdult ? ' *' : ''}`}
            required={isAdult}
            invalid={fieldErrors.Email != null}
            value={email}
            onChange={evt => setEmail(evt.target.value)}
          />
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label='Address 1 *'
            required
            invalid={fieldErrors.Address1 != null}
            value={address1}
            onChange={evt => setAddress1(evt.target.value)}
          />
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label='Address 2'
            invalid={fieldErrors.Address2 != null}
            value={address2}
            onChange={evt => setAddress2(evt.target.value)}
          />
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label='City *'
            required
            invalid={fieldErrors.City != null}
            value={city}
            onChange={evt => setCity(evt.target.value)}
          />
        </div>

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <Select
                className='cell'
                label='State *'
                required
                invalid={fieldErrors.State != null}
                options={stateOptions}
                setter={setState}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label='Zip Code *'
                required
                invalid={fieldErrors['Zip Code'] != null}
                value={zipCode}
                onChange={evt => setZipCode(evt.target.value)}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-100'>
            <Select
              className='width-100-percent'
              label='State *'
              required
              invalid={fieldErrors.State != null}
              options={stateOptions}
              setter={setState}
            />
            <TextField
              className='width-75-percent'
              label='Zip Code *'
              required
              invalid={fieldErrors['Zip Code'] != null}
              value={zipCode}
              onChange={evt => setZipCode(evt.target.value)}
            />
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={`Phone${isAdult ? ' *' : ''}`}
                required={isAdult}
                invalid={fieldErrors['Phone Number'] != null}
                value={phoneNumber}
                onChange={evt => setPhoneNumber(evt.target.value)}
                onBlur={(evt) => setPhoneNumber(formatPhoneNumber(evt.target.value))}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label={`Phone Type${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`}
                required={phoneNumber !== ''}
                fieldErrors={fieldErrors}
                options={phoneTypeOptions}
                setter={setPhoneType}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label={`Phone${isAdult ? ' *' : ''}`}
              required={isAdult}
              invalid={fieldErrors['Phone Number'] != null}
              value={phoneNumber}
              onChange={evt => setPhoneNumber(evt.target.value)}
              onBlur={(evt) => setPhoneNumber(formatPhoneNumber(evt.target.value))}
              fullwidth
            />
            <Select
              className='width-100-percent'
              label={`Phone Type${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`}
              required={phoneNumber !== ''}
              fieldErrors={fieldErrors}
              options={phoneTypeOptions}
              setter={setPhoneType}
            />
          </div>
        )}

        <>
          <div className='row notice'>Please tell us about health concerns or allergies.</div>

          <div className='row'>
            <TextField
              className='cell'
              name='Notes'
              label='Notes'
              textarea
              invalid={fieldErrors.Notes != null}
              value={notes}
              onChange={evt => setNotes(evt.target.value)}
            />
          </div>
        </>

        <div className='row'>
          <Checkbox
            checked={hasAttendedBefore}
            onChange={(evt) => setHasAttendedBefore(evt.target.checked)}
            label='Please check the box if this student has attended Francis Tuttle classes before'
          />
        </div>
      </div>

      {contacts.map((contact, contactIndex) =>
        <ContactForm
          key={`contact-${contactIndex}`}
          fieldErrors={fieldErrors}
          contact={contact}
          updateContact={updateContact.bind(null, contactIndex)}
          removeContactLabel={(currentAge != null && currentAge >= 18) || contacts.length > 1 ? 'Remove' : 'Reset'}
          removeContact={
            ((currentAge != null && currentAge >= 18) || contacts.length > 1)
              ? removeContact.bind(null, contactIndex)
              : () => { updateContact(contactIndex, Object.assign({}, blankContact)); props.dispatch({ type: 'DEFAULT_CONTACT', data: blankContact }) }
          }
          contactIndex={contactIndex}
          currentAge={currentAge}
          screenSize={screenSize}
          totalNumberOfContacts={contacts.length}
        />
      )}

      <div className='page-body centered'>
        <Button
          outlined
          icon='contact_phone'
          onClick={addContact}
          label={`Add ${contacts.length < 1 ? 'A' : 'Another'} Contact`}
        />
      </div>

      {isAdult &&
        <CompanySponsorshipForm
          companySponsorshipInfo={companySponsorshipInfo}
          setCompanySponsorshipInfo={setCompanySponsorshipInfo}
          fieldErrors={fieldErrors}
          screenSize={screenSize}
        />}

      <div className='page-body centered'>
        <Button
          unelevated
          disabled={saveButtonDisabled}
          label='Save and Continue'
          onClick={
            wrapButtonToggle(setSaveButtonDisabled, () => saveStudent(props, getStudent(), companySponsorshipInfo, setGlobalError, setFieldErrors, isCartLink))
          }
          theme={['primaryBg']}
        />
      </div>

    </div>
  )
}

function ContactForm (props) {
  const {
    fieldErrors,
    contact,
    contactIndex,
    updateContact,
    removeContact,
    removeContactLabel,
    currentAge,
    screenSize,
    totalNumberOfContacts
  } = props
  const prefix = `Contact ${contactIndex + 1}`
  const contactIsRequired = currentAge === null || currentAge < 18

  const index = contactIndex + 1
  const autoFocusAfterFirstContact = (index === totalNumberOfContacts) && index > 1

  return (
    <div>
      <div className='page-header'>
        <div className='contents'>
          <span className='max'>Related Contact</span>
          <button className='reset-button' onKeyDown={(e) => { if (e.key === 'Enter') removeContact() }}><SVGIcon icon='trashcan' className='none' onClick={removeContact} /></button>
          <span className='none' onClick={removeContact}>{removeContactLabel}</span>
        </div>
      </div>

      <div className='page-body'>

        {contactIsRequired && contactIndex === 0 && (
          <div className='contact-requirements notice'>
            'Please designate at least one Emergency Contact (and a Primary Contact for minor children).'
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label='First Name *'
                required
                invalid={fieldErrors[`${prefix} First Name`] != null}
                value={contact.firstName}
                onChange={evt => updateContact(Object.assign(contact, { firstName: evt.target.value }))}
                autoFocus={autoFocusAfterFirstContact}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label='Last Name *'
                required
                invalid={fieldErrors[`${prefix} Last Name`] != null}
                value={contact.lastName}
                onChange={evt => updateContact(Object.assign(contact, { lastName: evt.target.value }))}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row'>
            <TextField
              className='cell'
              label='First Name *'
              required
              invalid={fieldErrors[`${prefix} First Name`] != null}
              value={contact.firstName}
              onChange={evt => updateContact(Object.assign(contact, { firstName: evt.target.value }))}
              autoFocus={autoFocusAfterFirstContact}
            />
            <TextField
              className='cell'
              label='Last Name *'
              required
              invalid={fieldErrors[`${prefix} Last Name`] != null}
              value={contact.lastName}
              onChange={evt => updateContact(Object.assign(contact, { lastName: evt.target.value }))}
            />
          </div>
        )}

        <div className='row'>
          <TextField
            className='cell'
            type='email'
            label='Email *'
            required
            invalid={fieldErrors[`${prefix} Email`] != null}
            value={contact.email}
            onChange={evt => updateContact(Object.assign(contact, { email: evt.target.value }))}
          />
        </div>

        <div className='row anchor-width-100'>
          <Select
            className='width-100-percent'
            name={`${prefix} Relationship`}
            label='Relationship *'
            required
            fieldErrors={fieldErrors}
            options={relationshipOptions}
            onChange={evt => updateContact(Object.assign(contact, { relationship: evt.value }))}
          />
        </div>

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label='Primary Phone *'
                invalid={fieldErrors[`${prefix} Primary Phone`] != null}
                required
                value={contact.primaryPhone.number}
                onChange={evt => updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, number: evt.target.value } }))}
                onBlur={(evt) => updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, number: formatPhoneNumber(evt.target.value) } }))}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                name={`${prefix} Primary Phone Type`}
                label='Type *'
                required
                fieldErrors={fieldErrors}
                options={phoneTypeOptions}
                onChange={evt => { updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, type: evt.value } })) }}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label='Primary Phone *'
              invalid={fieldErrors[`${prefix} Primary Phone`] != null}
              required
              value={contact.primaryPhone.number}
              onChange={evt => updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, number: evt.target.value } }))}
              onBlur={(evt) => updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, number: formatPhoneNumber(evt.target.value) } }))}
              fullwidth
            />
            <Select
              className='width-100-percent'
              name={`${prefix} Primary Phone Type`}
              label='Type *'
              required
              fieldErrors={fieldErrors}
              options={phoneTypeOptions}
              onChange={evt => { updateContact(Object.assign(contact, { primaryPhone: { ...contact.primaryPhone, type: evt.value } })) }}
            />
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label='Secondary Phone'
                invalid={fieldErrors[`${prefix} Secondary Phone`] != null}
                value={contact.secondaryPhone.number}
                onChange={evt => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, number: evt.target.value } })) }}
                onBlur={(evt) => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, number: formatPhoneNumber(evt.target.value) } })) }}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label={`Type${contact.secondaryPhone.number != null && contact.secondaryPhone.number !== '' ? ' *' : ''}`}
                fieldErrors={fieldErrors}
                options={phoneTypeOptions}
                onChange={evt => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, type: evt.value } })) }}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label='Secondary Phone'
              invalid={fieldErrors[`${prefix} Secondary Phone`] != null}
              value={contact.secondaryPhone.number}
              onChange={evt => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, number: evt.target.value } })) }}
              onBlur={(evt) => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, number: formatPhoneNumber(evt.target.value) } })) }}
              fullwidth
            />
            <Select
              className='width-100-percent'
              label={`Type${contact.secondaryPhone.number != null && contact.secondaryPhone.number !== '' ? ' *' : ''}`}
              fieldErrors={fieldErrors}
              options={phoneTypeOptions}
              onChange={evt => { updateContact(Object.assign(contact, { secondaryPhone: { ...contact.secondaryPhone, type: evt.value } })) }}
            />
          </div>
        )}

        <div className='row'>
          <Checkbox
            id={`${prefix}-opt-out`}
            checked={contact.optOutOfTextMessages}
            onChange={(evt) => { updateContact(Object.assign(contact, { optOutOfTextMessages: evt.target.checked })) }}
            label='Opt out of Text Messages?'
          />
        </div>

        <div className='row'>
          <Checkbox
            id={`${prefix}-primary-adult`}
            checked={contact.isPrimaryAdult}
            onChange={(evt) => { updateContact(Object.assign(contact, { isPrimaryAdult: evt.target.checked })) }}
          >
            Primary Contact<span>(Will be contacted for enrollment-related issues for minor children)</span>
          </Checkbox>

        </div>

        <div className='row'>
          <Checkbox
            id={`${prefix}-emergency-contact`}
            checked={contact.isEmergencyContact}
            onChange={(evt) => { updateContact(Object.assign(contact, { isEmergencyContact: evt.target.checked })) }}
            label='Emergency Contact'
          />
        </div>

        <div className='row'>
          <TextField
            name={`${prefix} Notes`}
            label='Notes'
            textarea
            invalid={fieldErrors[`${prefix} Notes`] != null}
            value={contact.notes}
            onChange={evt => { updateContact(Object.assign(contact, { notes: evt.target.value })) }}
          />
        </div>

      </div>
    </div>
  )
}

function CompanySponsorshipForm ({ companySponsorshipInfo, setCompanySponsorshipInfo, sponsoringCompanies, fieldErrors, translationLanguage, screenSize }) {
  const companySupported =
    companySponsorshipInfo.companyGaveTimeOff ||
    companySponsorshipInfo.companyIsPaying ||
    companySponsorshipInfo.companyWillReimburse

  const selfEmploymentComponent = (
    !companySponsorshipInfo.selfEmployed && (
      <>
        <div className='row'>
          <TextField
            className='cell'
            label={'Supervisor\'s Name *'}
            value={companySponsorshipInfo.supervisorName}
            invalid={fieldErrors['Supervisor Name'] != null}
            onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorName: evt.target.value })}
            required={companySupported && !companySponsorshipInfo.selfEmployed}
          />
        </div>

        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row'>
            <TextField
              className='cell'
              label={'Supervisor\'s Phone Number *'}
              value={companySponsorshipInfo.supervisorPhoneNumber}
              invalid={fieldErrors['Supervisor Phone Number'] != null}
              onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: evt.target.value })}
              onBlur={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: formatPhoneNumber(evt.target.value) })}
              required={companySupported && !companySponsorshipInfo.selfEmployed}
            />

            <TextField
              className='cell'
              label={'Supervisor\'s Email *'}
              value={companySponsorshipInfo.supervisorEmail}
              invalid={fieldErrors['Supervisor Email'] != null}
              onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorEmail: evt.target.value })}
              required={companySupported && !companySponsorshipInfo.selfEmployed}
            />
          </div>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={'Supervisor\'s Phone Number *'}
                value={companySponsorshipInfo.supervisorPhoneNumber}
                invalid={fieldErrors['Supervisor Phone Number'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: evt.target.value })}
                onBlur={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: formatPhoneNumber(evt.target.value) })}
                required={companySupported && !companySponsorshipInfo.selfEmployed}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={'Supervisor\'s Email *'}
                value={companySponsorshipInfo.supervisorEmail}
                invalid={fieldErrors['Supervisor Email'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorEmail: evt.target.value })}
                required={companySupported && !companySponsorshipInfo.selfEmployed}
              />
            </div>
          </>
        )}
      </>
    )
  )

  const companyComponent = (
    <>
      <div className='row'>
        <TextField
          className='cell'
          label='Company Name *'
          value={companySponsorshipInfo.companyName}
          invalid={fieldErrors['Company Name'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyName: evt.target.value })}
          required={companySupported}
        />
      </div>

      <div className='row'>
        <TextField
          className='cell'
          label='Company Address *'
          value={companySponsorshipInfo.companyAddress}
          invalid={fieldErrors['Company Address'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyAddress: evt.target.value })}
          required={companySupported}
        />
      </div>

      <div className='row'>
        <TextField
          className='cell'
          label='City *'
          value={companySponsorshipInfo.companyCity}
          invalid={fieldErrors['Company City'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyCity: evt.target.value })}
          required={companySupported}
        />
      </div>
      {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
        <div className='row'>
          <Select
            className='cell-3'
            label='State *'
            invalid={fieldErrors['Company State'] != null}
            options={stateOptions}
            setter={value => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyState: value })}
            required={companySupported}
          />
          <TextField
            className='cell-2'
            label='Zip Code *'
            value={companySponsorshipInfo.companyZipCode}
            invalid={fieldErrors['Company Zip Code'] != null}
            onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyZipCode: evt.target.value })}
            required={companySupported}
          />
        </div>
      )}
      {(screenSize === 'xs' || screenSize === 'sm') && (
        <>
          <div className='row'>
            <Select
              className='cell-3'
              label='State *'
              invalid={fieldErrors['Company State'] != null}
              options={stateOptions}
              setter={value => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyState: value })}
              required={companySupported}
            />
          </div>
          <div className='row'>
            <TextField
              className='cell-2'
              label='Zip Code *'
              value={companySponsorshipInfo.companyZipCode}
              invalid={fieldErrors['Company Zip Code'] != null}
              onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyZipCode: evt.target.value })}
              required={companySupported}
            />
          </div>
        </>
      )}
      <div className='row'>
        <Checkbox
          checked={companySponsorshipInfo.selfEmployed}
          invalid={fieldErrors['Self Employed'] != null}
          onChange={(evt) => setCompanySponsorshipInfo({
            ...companySponsorshipInfo,
            selfEmployed: evt.target.checked
          })}
          label='Are you self employed?'
        />
      </div>
      {selfEmploymentComponent}
    </>
  )

  const companyForm =
    <>
      <br />
      <br />
      {/* {isWedIndustryStudent && sponsorshipComponent}
      {isWedIndustryStudent && (
        <div className='row'>
          <Checkbox
            checked={companySponsorshipInfo.employedByDifferentCompany}
            invalid={fieldErrors['Employed By Different Company'] != null}
            onChange={(evt) => setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              employedByDifferentCompany: evt.target.checked
            })}
            label={translateFromEnglish('Are you currently employed by a different organization?', '¿Está actualmente empleado por una organización diferente?', translationLanguage)}
          />
        </div>
      )}

      {isWedIndustryStudent && companySponsorshipInfo.employedByDifferentCompany ? companyComponent : ''}
      {!isWedIndustryStudent && companyComponent} */}
      {companyComponent}
    </>

  return (
    <>
      <div className='page-header'>
        <div className='contents'>
          Enter Company Sponsorship Information
        </div>
      </div>

      <div className='page-body'>
        <div className='row'>
          <h4>Check any of the boxes below that apply to you:</h4>
        </div>

        <div className='row'>
          <Checkbox
            checked={companySponsorshipInfo.companyGaveTimeOff}
            invalid={fieldErrors.companyGaveTimeOff != null}
            onChange={(evt) => setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              takenForPersonalEnrichment: evt.target.checked ? false : companySponsorshipInfo.takenForPersonalEnrichment,
              companyGaveTimeOff: evt.target.checked
            })}
            label='My company gave me time off to take this class.'
          />
        </div>

        <div className='row'>
          <Checkbox
            checked={companySponsorshipInfo.companyIsPaying}
            invalid={fieldErrors.companyIsPaying != null}
            onChange={(evt) => setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              takenForPersonalEnrichment: evt.target.checked ? false : companySponsorshipInfo.takenForPersonalEnrichment,
              companyIsPaying: evt.target.checked
            })}
            label='My company is paying for this class.'
          />
        </div>

        <div className='row'>
          <Checkbox
            checked={companySponsorshipInfo.companyWillReimburse}
            invalid={fieldErrors.companyWillReimburse != null}
            onChange={(evt) => setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              takenForPersonalEnrichment: evt.target.checked ? false : companySponsorshipInfo.takenForPersonalEnrichment,
              companyWillReimburse: evt.target.checked
            })}
            label='My company will reimburse me for attending this class.'
          />
        </div>

        <div className='row'>
          <Checkbox
            checked={!companySupported}
            onChange={(evt) => {
              setCompanySponsorshipInfo({
                ...companySponsorshipInfo,
                companyGaveTimeOff: evt.target.checked ? false : companySponsorshipInfo.companyGaveTimeOff,
                companyIsPaying: evt.target.checked ? false : companySponsorshipInfo.companyIsPaying,
                companyWillReimburse: evt.target.checked ? false : companySponsorshipInfo.companyHiredIntoAssociatedPosition
              })
            }}
            label='None of the above.'
          />
        </div>

        {companySupported && companyForm}
      </div>
    </>
  )
}

// --functions
async function saveStudent (props, student, companySponsorshipInfo, setGlobalError, setFieldErrors, isCartLink) {
  const token = await recaptcha('saveStudent')

  if (!companySponsorshipInfo.companyName || !companySponsorshipInfo.companyAddress || !companySponsorshipInfo.companyCity || !companySponsorshipInfo.companyState || !companySponsorshipInfo.companyZipCode) {
    companySponsorshipInfo.companyName = companySponsorshipInfo.sponsoringCompanyName
    companySponsorshipInfo.companyAddress = companySponsorshipInfo.sponsoringCompanyAddress
    companySponsorshipInfo.companyCity = companySponsorshipInfo.sponsoringCompanyCity
    companySponsorshipInfo.companyState = companySponsorshipInfo.sponsoringCompanyState
    companySponsorshipInfo.companyZipCode = companySponsorshipInfo.sponsoringCompanyZipCode
  }

  const payload = preparePayload({ ...student, ...companySponsorshipInfo })
  payload.contacts.forEach(contact => {
    if (contact.secondaryPhone.number == null && contact.secondaryPhone.type == null) {
      delete contact.secondaryPhone
    }
  })
  Object.assign(payload, { recaptchaToken: token })

  try {
    const response = await http(setGlobalError, http => http.post('/api/SelfService/Students', payload))
    student.publicId = response.data.publicId
    student.contacts.forEach((contact, i) => {
      contact.publicId = response.data.contacts[i]
    })
    student.companySponsorshipInfo = companySponsorshipInfo

    props.dispatch({
      type: 'ADD_STUDENT',
      data: student
    })
    props.dispatch({
      type: 'DEFAULT_ADDRESS',
      data: student
    })
    if (student.contacts && student.contacts.length > 1) {
      props.dispatch({
        type: 'DEFAULT_CONTACT',
        data: student.contacts[0]
      })
    }
    setTimeout(() => {
      if (student.isWedIndustryStudent) {
        props.history.push('/students?isWedIndustry=true')
      } else if (isCartLink) {
        props.history.push('/cart')
      } else {
        props.history.push('/students')
      }
    }, 0)
  } catch (err) {
    if (err.response != null && err.response.status === 400) {
      const { data } = err.response
      setGlobalError('Please fix the errors on the page and try again.')
      if (data.validationErrors != null) {
        setFieldErrors(formatErrors(data, 'Contact'))
      } else {
        setFieldErrors({})
      }
    } else if (err.response != null && err.response.status === 422) {
      setGlobalError(err.response.data.message)
      setFieldErrors({})
    } else if (err.response != null && err.response.status === 424) {
      setGlobalError('It looks like you\'re going pretty fast! Please wait a moment and try again, or contact us at 405-717-4900 to enroll.')
      setFieldErrors({})
    } else {
      setFieldErrors({})
    }
  }
  window.scrollTo(0, 0)
}

// --mappings
export default connect(state => ({
  defaultAddress: state.defaultAddress,
  defaultContact: state.defaultContact
}))(AddStudentPage)
